import type { Frame } from '@pfe/design-renderer'

export type HandleName = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

/** Punkt um den Rahmenmittelpunkt zurückdrehen (Layer-lokale Koordinaten). */
export function unrotatePoint(px: number, py: number, frame: Frame): { x: number; y: number } {
  const cx = frame.xMm + frame.wMm / 2
  const cy = frame.yMm + frame.hMm / 2
  const a = (-frame.rotationDeg * Math.PI) / 180
  const dx = px - cx
  const dy = py - cy
  return { x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a) }
}

/**
 * Rahmen über einen Griff verändern. Delta ist bereits in mm und in Layer-lokale Achsen
 * gedreht. Bei `keepAspect` bleibt das Seitenverhältnis erhalten (Eckgriffe), der gegenüber-
 * liegende Punkt bleibt fix.
 */
export function resizeFrame(start: Frame, handle: HandleName, dxMm: number, dyMm: number, keepAspect: boolean, minMm = 3): Frame {
  let x = start.xMm
  let y = start.yMm
  let w = start.wMm
  let h = start.hMm
  const ar = start.wMm / Math.max(0.001, start.hMm)

  if (handle.includes('e')) w = Math.max(minMm, start.wMm + dxMm)
  if (handle.includes('w')) { w = Math.max(minMm, start.wMm - dxMm); x = start.xMm + (start.wMm - w) }
  if (handle.includes('s')) h = Math.max(minMm, start.hMm + dyMm)
  if (handle.includes('n')) { h = Math.max(minMm, start.hMm - dyMm); y = start.yMm + (start.hMm - h) }

  if (keepAspect) {
    const corner = handle.length === 2
    if (corner) {
      // dominante Achse bestimmt, die andere folgt
      const byW = w / ar
      const byH = h * ar
      if (Math.abs(w - start.wMm) >= Math.abs(h - start.hMm)) h = Math.max(minMm, byW)
      else w = Math.max(minMm, byH)
    } else if (handle === 'e' || handle === 'w') {
      h = Math.max(minMm, w / ar)
    } else {
      w = Math.max(minMm, h * ar)
    }
    if (handle.includes('w')) x = start.xMm + start.wMm - w
    if (handle.includes('n')) y = start.yMm + start.hMm - h
    if (!handle.includes('w') && !handle.includes('e')) x = start.xMm + (start.wMm - w) / 2
    if (!handle.includes('n') && !handle.includes('s')) y = start.yMm + (start.hMm - h) / 2
  }

  // Bei gedrehten Rahmen den Mittelpunkt so korrigieren, dass der fixe Gegenpunkt bleibt
  if (start.rotationDeg) {
    const a = (start.rotationDeg * Math.PI) / 180
    const dcx = (x + w / 2) - (start.xMm + start.wMm / 2)
    const dcy = (y + h / 2) - (start.yMm + start.hMm / 2)
    const rcx = dcx * Math.cos(a) - dcy * Math.sin(a)
    const rcy = dcx * Math.sin(a) + dcy * Math.cos(a)
    x = start.xMm + start.wMm / 2 + rcx - w / 2
    y = start.yMm + start.hMm / 2 + rcy - h / 2
  }

  return { ...start, xMm: round(x), yMm: round(y), wMm: round(w), hMm: round(h) }
}

/** Rotation aus dem Winkel Mittelpunkt->Zeiger, mit Einrasten bei 0/45/90… (±3°). */
export function rotationFromPointer(frame: Frame, pxMm: number, pyMm: number, snap = true): number {
  const cx = frame.xMm + frame.wMm / 2
  const cy = frame.yMm + frame.hMm / 2
  let deg = (Math.atan2(pyMm - cy, pxMm - cx) * 180) / Math.PI + 90
  deg = ((deg % 360) + 360) % 360
  if (snap) {
    const nearest = Math.round(deg / 45) * 45
    if (Math.abs(deg - nearest) <= 3) deg = nearest % 360
  }
  return round(deg)
}

export function round(v: number): number {
  return Math.round(v * 100) / 100
}
